/**
 * figureMatch — 「내가 누른 그림」과 「서버가 준 그림」이 같은 것인지 판정한다.
 *
 * 왜 필요한가: 계약 §1-1 의 `index` 는 **저장된 본문의 문서 순서**다. 화면이 센 값과 어긋나면
 * 서버는 거절하지 않고 **다른 그림에 성공적으로** 저장한다 — 200 이 오므로 화면은 「저장됐습니다」라고
 * 말하고, 사람은 엉뚱한 그림이 바뀐 것을 모른다. 조용히 망가지는 종류라 관문이 필요하다.
 *
 * 컴포넌트에서 떼어 둔 이유는 두 가지다 — 순수 판정이라 따로 검사할 수 있고,
 * 검사에서 화면 전체(axios·라우터까지)를 끌어오지 않아도 된다.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md (§1-1·§1-2·§8)
 * 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §2 (Q1·Q2·Q3·Q5·Q6 확정)
 */

/**
 * @param item 서버 목록의 그림 하나 (kind·src 를 본다)
 * @param nodeSrc 편집기에서 누른 노드의 현재 src
 * @returns { ok, reason, exact }
 *  - ok:false 면 쓰기를 막는다 (reason: 'missing' | 'kind' | 'src')
 *  - exact:true  바이트까지 같다
 *  - exact:null  **대조할 재료가 없다**(서버가 src 를 주지 않았다) — 종류만 보고 통과시킨 것
 *
 * 2026-08-29 회신으로 바이트 대조가 **판정으로 승격**됐다.
 * 종전에는 참고만 했다. 근거가 둘이었는데 회신이 둘 다 무너뜨렸다 —
 *   ① 「서버가 src 를 다듬어 줄 여지가 있다」 → Q6: 은행 `item.src` 는 **원문 그대로**(정규화 없음).
 *   ② 「OCR item 에는 src 자체가 없다」     → Q5: OCR 항목에 `src` 추가, 검수 상세의 image 노드
 *      `src` 와 **바이트 동일**(같은 함수로 만듦·e2e 확인). 대조법도 `node.src === item.src` 로 명시.
 * 즉 이제 바이트가 다르면 서버가 다듬은 것이 아니라 **문서가 다른 것**이다. 그래서 막는다.
 */
export default function matchFigure(item, nodeSrc) {
  if (!item) return { ok: false, reason: 'missing', exact: null };
  const src = typeof nodeSrc === 'string' ? nodeSrc : '';
  const isSvgNode = /^data:image\/svg\+xml/i.test(src);
  const isSvgItem = item.kind === 'svg';
  // 종류가 다르면 확실히 다른 그림이다 — 여기서 막는다.
  if (src && isSvgNode !== isSvgItem) return { ok: false, reason: 'kind', exact: null };
  // 서버가 대조용 src 를 주지 않은 그림 — 회신 Q3 꼬리가 「대조 대상 아님」으로 못박은 자리다
  // (원본 페이지 이미지가 만료된 픽셀 그림은 `src:null` 로 온다). 옛 배포분처럼 키 자체가
  // 없는 경우도 같은 갈래로 받는다 — 둘을 화면이 가를 방법이 없고, 없는 대조를 있는 척하지 않는다.
  // 여기서 막으면 만료 그림이 통째로 편집 불가가 되므로 **명시적으로** 통과시킨다.
  if (item.src == null) return { ok: true, reason: null, exact: null };
  // 회신 Q5 가 지정한 대조법 그대로 — `node.src === item.src`. 다만 **막지 않는다.**
  //
  // 2026-08-30 정정: 한때 여기서 ok:false 로 막았는데 그 판정이 실화면에서 **정상 그림까지 잠갔다**
  // (오너 보고: 「그림 편집 모달은 실행되는데 수정할 수 없다」). writable 이 false 가 되면서
  // 명세 폼·저장·AI 재요청·되돌리기·삭제가 한꺼번에 비활성이 됐다.
  //
  // 회신이 「막아라」고 한 자리는 **Q3 의 편집본 하나뿐**이다 — Q5 는 대조 *방법*을 준 것이고,
  // 어긋났을 때 무엇을 하라는 지시가 아니었다. 그래서 사유만 남기고 통과시킨다:
  // 순번이 실제로 어긋났는지는 **개수와 종류**가 잡고(그 둘은 계속 막는다), 바이트는 참고다.
  // 편집본에서의 차단은 호스트가 lexicalEdited 와 이 exact 를 함께 보고 판단한다(FigureModal).
  if (item.src !== src) return { ok: true, reason: 'src', exact: false };
  return { ok: true, reason: null, exact: true };
}

/**
 * 문서의 `image` 노드를 **원본 그대로** 문서 순서로 훑는다 — 세는 규칙이 사는 유일한 자리다.
 *
 * `collectFigureNodes` 가 이것을 쓴다. 순회를 따로 두면 계약 §8 의 규칙(「`$dfs` 가 닿는
 * `type:'image'` 전부, 캡션 제외, `src` 없는 노드도 셈」)이 한쪽만 고쳐지고,
 * 그러면 대조가 서로 다른 그림을 세게 된다.
 */
function walkImageNodes(editorState) {
  let root = null;
  try {
    // 서버가 **문자열로도 객체로도** 준다. 실측 2026-08-30: `GET /api/v2/problems/61592` 의
    // `body_lexical` 은 `typeof === 'object'` 였다. 종전에는 `JSON.parse(String(x))` 라
    // 객체가 `"[object Object]"` 가 되어 늘 파싱에 실패했고, 그 결과가 **빈 배열**이었다.
    const doc = typeof editorState === 'string' ? JSON.parse(editorState) : editorState;
    root = doc?.root ?? null;
  } catch (_) {
    root = null;
  }
  // **읽지 못한 것과 「그림이 0장인 문서」를 가른다.** 종전에는 둘 다 빈 배열이라,
  // 문서를 못 읽으면 화면이 0장을 세고 서버가 1장을 세어 그 field 의 그림 편집이 통째로 잠겼다
  // (오너 보고 2026-08-30 「모달은 뜨는데 수정할 수 없다」 — 노란 띠가 「화면에는 0장, 서버는 1장」).
  // null 을 돌려주면 호출부의 줄 대조가 **검사를 건너뛴다**(matchFigureSequence 의 checked:false).
  // 모르는 것으로 막지 않는다 — 이 저장소가 폴더 잠금(is_mine)·보관 원본(has_original)에서 쓰는 원칙과 같다.
  if (!root) return null;
  const out = [];
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'image') out.push(node);
    const kids = Array.isArray(node.children) ? node.children : [];
    kids.forEach(walk);
  };
  walk(root);
  return out;
}

/**
 * 편집기 문서(Lexical editorState 문자열)의 그림 노드를 **문서 순서로** 훑는다.
 * 계약 §1-1 의 `index` 와 같은 순서다(중첩 포함).
 *
 * 세는 규칙은 **`type:'image'` 전부**다. src 가 문자열이 아닌 노드도 센다 —
 * 회신 §2 Q2 가 「셉니다(양쪽 동일하게 `type==='image'` 전부)」로 확정했고, 계약 §8 의 순회 규칙에도
 * 「`src` 없는 image 노드는 셈」이 들어갔다. 그런 노드는 서버 목록에 `kind:'pixel'`·`src:null` 로 온다.
 * 화면만 빼면 그 노드 **뒤의 모든 그림이 한 칸씩 어긋나고** 개수도 1 이 모자라, 그 field 의 그림
 * 편집이 통째로 잠긴다(안내가 시키는 [저장]으로도 풀리지 않는다 — 원인이 문서가 아니라 세는 규칙이다).
 *
 * 캡션 안 그림은 양쪽 다 세지 않는다(회신 Q1 — 서버가 FE 에 맞췄다). 여기서 따로 거를 필요가 없는
 * 이유는 캡션이 `children` 이 아니라 `caption.editorState` 로 직렬화되기 때문이다.
 *
 * 대조에만 쓰므로 자리(path)는 담지 않는다 — 노드를 다시 찾아 값을 갈아 끼우는 쪽은
 * ProblemViewTab.collectImageNodes 가 따로 한다(그쪽은 path 가 필요하다).
 */
export function collectFigureNodes(editorState) {
  const nodes = walkImageNodes(editorState);
  // 필터가 아니라 정규화다 — 문자열이 아니면 null 로 담아 「대조할 값이 없는 그림」임을 남긴다.
  return nodes && nodes.map((n) => ({ src: typeof n.src === 'string' ? n.src : null }));
}

/**
 * 이 그림을 **서버가 알고 있는가** — 저장본 문서의 image 노드 src 집합에 그 src 가 있는가.
 *
 * 쓰는 자리는 하나다: 편집기에서 그림을 지우려 할 때 그것을 삭제 API(DELETE)로 돌릴지,
 * 편집기가 그냥 지우게 둘지 가르는 판정(→ FigureEditContext 의 onAction 반환값).
 * 회신 §2 Q8 이 금지한 것은 「**서버가 세고 있는** 그림을 본문 PATCH 로 소리 없이 없애는 것」이고,
 * 방금 붙여넣어 서버가 아직 모르는 그림에는 그 위험이 없다.
 *
 * `nodes` 는 **저장본**(상세 응답의 lexical)에서 뽑은 collectFigureNodes 결과여야 한다.
 * 라이브 편집기에서 뽑으면 방금 넣은 그림도 「있다」가 되어 판정 자체가 무의미해진다.
 *
 * ── 알고 남기는 한계 ──
 * 판정 재료가 src 뿐이라 **같은 그림을 두 번 넣어 src 가 겹치면** 새로 넣은 쪽도 「있다」로 읽혀
 * 삭제 창으로 돌아간다. 그 창은 저장본 기준 순번을 대조하다 막고 이유를 말한다 —
 * **막고 안내하는 쪽의 오류**라 이대로 둔다. 반대 방향(서버가 아는 그림을 「없다」로 읽는 것)은
 * 조용히 이력이 어긋나는 사고라, 가를 수 없는 값은 전부 「있다」로 떨어뜨린다.
 */
export function isSavedFigureSrc(nodes, src) {
  // 대조할 재료가 없으면 「서버가 아는 그림」으로 본다 — 모르는 것을 「새 그림」이라 단정하지 않는다.
  if (!Array.isArray(nodes)) return true;
  if (typeof src !== 'string' || src === '') return true;
  return nodes.some((n) => n && n.src === src);
}

/**
 * **누른 그림을 서버 목록에서 찾는다** — 화면이 센 순번이 아니라 그림 자체(`src`)로.
 *
 * 왜 필요한가: 2026-09-04 회신 §5 가 OCR 의 `index` 를 **payload(원문)의 문서 순서**로 못박았다.
 * 그런데 편집기가 넘겨주는 순번은 **편집본의 문서 순서**다(ImageComponent 의 `$figureIndexOfKey`).
 * 검수자가 본문에 참고 이미지를 하나 넣어 두면 그 뒤 그림이 전부 한 칸씩 밀려, 화면이 센 값으로
 * 쓰면 **엉뚱한 그림에 저장되고 서버는 200 을 준다.**
 *
 * 대조값은 `src` 뿐이고 그것으로 충분하다 — 회신 §2 Q5·Q6 이 OCR `item.src` 를 검수 상세의
 * image 노드 `src` 와 **바이트 동일**로 확정했고, 2026-09-04 회신 §2 는 조회 때도 그 값을
 * 현재 렌더로 맞춰 준다고 했다.
 *
 * ── 못 찾는 경우를 아는 척하지 않는다 ──
 *  · `duplicate` 같은 그림이 그 field 에 둘 이상이다. **원리적으로 못 가른다** — 서버가 주는
 *    대조값이 src 뿐이라 두 자리 중 어느 것인지 정할 방법이 없다. 막고 호출부가 안내한다.
 *  · `missing`   그 src 가 서버 목록에 없다. 검수자가 본문에 직접 넣은(서버가 모르는) 그림이거나,
 *                내가 읽은 뒤 서버에서 그림이 바뀐 것이다.
 *  · `no_src`    대조할 재료가 없다(노드 src 가 비었거나, 서버가 `src:null` 만 준다).
 *
 * @returns { ok:true, item, index } | { ok:false, reason }
 */
export function findFigureBySrc(items, field, src) {
  if (!Array.isArray(items)) return { ok: false, reason: 'no_src' };
  if (typeof src !== 'string' || src === '') return { ok: false, reason: 'no_src' };
  // `src == null` 인 항목(만료 픽셀·옛 배포분)은 대조 대상이 아니다 — 후보에서 빼야
  // 「없다」와 「모른다」가 섞이지 않는다.
  const same = items.filter((it) => it && it.field === field && typeof it.src === 'string' && it.src === src);
  if (same.length === 1) return { ok: true, item: same[0], index: Number(same[0].index) };
  return { ok: false, reason: same.length === 0 ? 'missing' : 'duplicate' };
}

/**
 * **본문에서 사라진 그림을 찾아 삭제 순서를 짠다** — 저장할 때 함께 지우기 위한 계획서다.
 *
 * ── 왜 필요한가 ──
 * 편집기에서 그림을 백스페이스로 지우고 저장하면 종전에는 관문이 막았다. 근거는 회신 §2 Q8 —
 * 본문 PATCH 로 노드를 없애면 서버가 그 사실을 몰라 **지운 그림의 이력이 남고 뒤 그림들의
 * 순번이 밀린다**. 그런데 그 안내가 시키는 [삭제] 버튼도 같은 창의 순번 대조에 막혀 있어,
 * 두 관문이 서로를 가리키는 순환이 됐다(오너 보고 2026-09-04).
 * 막는 대신 **어떤 방법으로 지우든 삭제 API 를 타게** 하면 이력이 맞고 순환도 사라진다.
 *
 * ── 세는 자를 개수에서 `src` 로 바꾼다 ──
 * 개수로 재면 「한 장 지우고 한 장 붙여넣어 수가 같아지는」 경우를 놓친다. 그때 지워진 그림의
 * 이력이 유령으로 남아 뒤 그림의 순번이 밀린다 — 종전 관문이 알고 남긴 구멍이고, 여기서 닫는다.
 *
 * ── 같은 그림이 여럿일 때는 **자리로** 가른다 (2026-09-04) ──
 * 서버 사본 수 − 문서 사본 수가 그 그림의 삭제 수다. 어느 사본인지는 문서를 앞에서부터 서버 줄에
 * 맞춰 보고(순서대로 짝짓기) **짝이 안 된 사본**으로 정한다. 사본끼리는 바이트가 같아 어느 것을
 * 지워도 본문은 같고, 다른 것은 어느 이력이 남는가뿐이다 — 그것으로 저장을 막지 않는다.
 * 자리만 옮긴 그림(드래그)은 수가 그대로라 삭제 후보가 아니다 — 뒤에서 못 찾으면 앞쪽 사본을 물린다.
 *
 * 종전에는 「같은 그림이 둘인데 하나만 사라짐」을 `duplicate` 로 막고 [삭제]를 쓰라고 했는데,
 * 그 [삭제]가 다시 저장을 요구해 사람이 같은 자리를 맴돌았다(오너 보고 2026-09-04).
 *
 * ── 못 하는 것을 아는 척하지 않는다 ──
 *  · `no_src`    서버가 `src` 를 주지 않는 그림(만료 픽셀)은 개수로만 잰다 — 문서의 src 없는
 *                노드와 수를 맞춰 보고, **일부만** 사라졌으면 어느 것인지 못 가려 막는다.
 *                전부 그대로거나 전부 사라졌으면 막지 않는다.
 *  · `unknown`   목록이나 문서를 읽지 못했다.
 *
 * **호출 전에 문서가 최신인지 확인해야 한다** — 그림 창에서 방금 고쳐 서버가 앞서 있으면
 * 「서버 src 가 문서에 없다」가 「지워졌다」로 오판된다. 그 판정은 호출부가 `updated_at` 으로 한다.
 *
 * @returns { ok:true, targets:number[] } — targets 는 **순번이 큰 것부터**(앞 순번이 밀리지 않게)
 *          | { ok:false, reason }
 */
export function planFigureDeletions(items, nodes, field) {
  if (!Array.isArray(items) || !Array.isArray(nodes)) return { ok: false, reason: 'unknown' };
  const hasSrc = (v) => typeof v === 'string' && v !== '';
  const mine = items.filter((it) => it && it.field === field)
    .slice().sort((a, b) => Number(a.index) - Number(b.index));
  const known = mine.filter((it) => hasSrc(it.src));
  const blind = mine.filter((it) => !hasSrc(it.src));
  const docKnown = nodes.filter((n) => hasSrc(n?.src));
  const docBlind = nodes.length - docKnown.length;

  // ① src 별 삭제 수 = 서버 사본 수 − 문서 사본 수(양수만).
  const tally = (arr, get) => arr.reduce((m, v) => m.set(get(v), (m.get(get(v)) || 0) + 1), new Map());
  const docCount = tally(docKnown, (n) => n.src);
  const deficit = new Map();
  tally(known, (it) => it.src).forEach((sc, src) => {
    const d = sc - (docCount.get(src) || 0);
    if (d > 0) deficit.set(src, d);
  });

  // ② 어느 사본인가 — 문서 순서대로 서버 줄에 짝을 지어 두고, 짝이 안 된 사본을 지운다.
  const matched = new Set();
  let cursor = 0;
  docKnown.forEach((n) => {
    for (let k = cursor; k < known.length; k += 1) {
      if (!matched.has(k) && known[k].src === n.src) { matched.add(k); cursor = k + 1; return; }
    }
    // 뒤에서 못 찾았다 — 자리를 옮긴 그림이다. 앞쪽 사본을 물려 삭제 후보에서 뺀다.
    for (let k = 0; k < cursor; k += 1) {
      if (!matched.has(k) && known[k].src === n.src) { matched.add(k); return; }
    }
  });
  const targets = [];
  known.forEach((it, k) => {
    const d = deficit.get(it.src) || 0;
    if (d > 0 && !matched.has(k)) { targets.push(Number(it.index)); deficit.set(it.src, d - 1); }
  });

  // ③ src 없는 그림은 개수로만 — 문서의 src 없는 노드와 수를 맞춘다.
  const blindGone = blind.length - Math.min(docBlind, blind.length);
  if (blindGone > 0) {
    if (blindGone !== blind.length) return { ok: false, reason: 'no_src' };
    blind.forEach((it) => targets.push(Number(it.index)));
  }
  return { ok: true, targets: targets.sort((a, b) => b - a) };
}

/**
 * 새 그림을 **서버의 몇 번째 자리**에 넣을 것인가 — 등록 API 의 `at`(계약 §10 · 회신 §1).
 *
 * 붙여넣은 자리 앞에 있는 편집기 그림들 중 **서버가 아는 것만** 센다. 편집본에는 서버가 세지
 * 않는 참고 이미지가 섞여 있을 수 있고(회신 §5), 그것까지 세면 새 그림이 한 칸 뒤로 밀린다.
 *
 * `precedingSrcs` 가 null 이면(선택을 읽지 못했다) **null 을 돌려준다** — 호출부는 `at` 을
 * 싣지 않고, 서버는 그때 맨 끝에 넣는다. 모르는 자리를 0으로 단정해 맨 앞에 꽂지 않는다.
 */
export function countKnownBefore(precedingSrcs, items, field) {
  if (!Array.isArray(precedingSrcs) || !Array.isArray(items)) return null;
  const known = new Set(
    items.filter((it) => it && it.field === field && typeof it.src === 'string' && it.src !== '')
      .map((it) => it.src),
  );
  return precedingSrcs.filter((s) => typeof s === 'string' && known.has(s)).length;
}

/**
 * 줄 맞춰보기 — 서버가 세는 그림 줄과 화면이 그리는 그림 줄이 **같은 순서인가**.
 *
 * matchFigure 는 「내가 누른 그 자리」만 본다. 그래서 그림이 통째로 한 칸 밀린 경우,
 * 그 자리의 종류가 우연히 같으면 통과해 버린다. 여기서 줄 전체를 나란히 놓아 그것을 잡는다.
 *
 * 잡는 것
 *   개수 — 그림을 지우거나 더한 문제. 순서가 어긋나는 가장 흔한 경로이고 확실하다.
 *   종류 — 자리마다 도판(svg)/원본(pixel)이 같은가.
 *   바이트 — 자리마다 `node.src === item.src` 인가(회신 §2 Q5·Q6 로 확정된 대조법).
 *     **같은 종류의 그림이 자리만 바뀐 경우**를 이것이 닫는다 — 종전에는 알고 남긴 구멍이었다.
 * 못 잡는 것
 *   서버가 `src` 를 주지 않는 그림(만료 픽셀·옛 배포분). 그 자리는 종류만 본다.
 *
 * nodes 를 주지 않으면 검사하지 않고 통과시킨다(checked:false) — 없는 그물이 있는 척하지 않는다.
 */
export function matchFigureSequence(items, nodes) {
  if (!Array.isArray(items) || !Array.isArray(nodes)) {
    return { ok: true, reason: null, checked: false };
  }
  const serverCount = items.length;
  const docCount = nodes.length;
  if (serverCount !== docCount) {
    /**
     * **개수가 왜 어긋났는지까지 센다** — 사람이 할 일이 갈리기 때문이다.
     *
     * 문서에만 있는 그림(서버가 모르는 src)의 수가 차이와 정확히 같으면, 원인은 하나다:
     * **검수자가 본문에 직접 넣은 이미지.** 그때는 [저장]해도 풀리지 않는다(회신 §5 —
     * 편집본에만 있는 image 노드를 서버는 세지 않는다). 반대로 그 수가 맞지 않으면 원인이
     * 섞인 것이라 종전 문구가 맞다.
     *
     * 대조할 재료가 하나라도 비면(`src:null` 인 만료 그림 등) **세지 않는다** — null 로 두어
     * 문구가 「모른다」 쪽 갈래를 고르게 한다. 모르는 것을 0으로 세면 화면이 원인을 단정한다.
     */
    const comparable = items.every((i) => typeof i?.src === 'string' && i.src !== '')
      && nodes.every((n) => typeof n?.src === 'string' && n.src !== '');
    let unknownInDoc = null;
    if (comparable) {
      const serverSrcs = new Set(items.map((i) => i.src));
      unknownInDoc = nodes.filter((n) => !serverSrcs.has(n.src)).length;
    }
    return { ok: false, reason: 'count', checked: true, serverCount, docCount, unknownInDoc };
  }
  // 바이트가 다른 첫 자리. **막지는 않고** 사실만 나른다(matchFigure 의 2026-08-30 정정과 짝) —
  // 편집본에서의 차단은 호스트가 이 값과 lexicalEdited 를 함께 보고 판단한다.
  let inexactAt = null;
  for (let i = 0; i < serverCount; i += 1) {
    const one = matchFigure(items[i], nodes[i]?.src);
    if (!one.ok) {
      // 사유를 그대로 싣는다 — 'kind' 로 뭉개면 화면이 「종류가 다릅니다」라는 거짓을 말한다.
      return { ok: false, reason: one.reason, checked: true, serverCount, docCount, at: i };
    }
    if (one.exact === false && inexactAt == null) inexactAt = i;
  }
  return { ok: true, reason: null, checked: true, serverCount, docCount, inexactAt };
}
